import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { paragraphsWithStyle, readDocument } from "@/office/word/document";

export const lektionWeitereVorlagen: Lesson = {
  id: "weitere-vorlagen",
  title: "Titel, Untertitel und Zitate",
  summary: "Die übrigen Standardvorlagen und wofür sie gedacht sind.",
  durationMinutes: 7,
  learningGoals: [
    "Titel und Untertitel von Überschriften unterscheiden",
    "Zitatvorlagen einsetzen",
    "Vorlagen nach ihrer Bedeutung auswählen",
  ],
  steps: [
    {
      id: "titel-und-untertitel",
      title: "Titel ist keine Überschrift 1",
      explanation: [
        "Für die Dokumentüberschrift ganz oben gibt es eine eigene Vorlage: „Titel“, " +
          "ergänzt um „Untertitel“ für eine Beschreibung darunter. Beide sind bewusst " +
          "keine Überschriftenebenen.",
        "Der Grund liegt im Inhaltsverzeichnis: Dort sollen die Kapitel stehen, nicht der " +
          "Name des Dokuments. Ein Titel in der Vorlage „Überschrift 1“ würde als erstes " +
          "Kapitel auftauchen – und wäre in der Gliederung ein Kapitel, das alle anderen " +
          "enthält.",
      ],
      keyPoints: [
        "„Titel“ = Name des Dokuments, „Überschrift 1“ = erstes Kapitel.",
        "Titel und Untertitel erscheinen nicht im Inhaltsverzeichnis.",
      ],
      task: {
        instruction:
          "Setze ganz an den Anfang deines Dokuments eine Zeile in der Vorlage „Titel“ " +
          "und darunter eine Zeile in der Vorlage „Untertitel“.",
        hint: "Cursor in die erste Zeile, dann im Katalog „Formatvorlagen“ nach „Titel“ suchen – eventuell musst du den Katalog über den Pfeil aufklappen.",
        check: async () => {
          const snapshot = await readDocument();
          const titel = paragraphsWithStyle(snapshot, "Title");
          const untertitel = paragraphsWithStyle(snapshot, "Subtitle");
          const erster = snapshot.filledParagraphs[0];

          return fromCriteria(
            [
              criterion("Ein Absatz verwendet die Vorlage „Titel“", titel.length > 0),
              criterion(
                "Der Titel steht ganz oben im Dokument",
                titel.length > 0 && titel[0].index === erster?.index,
              ),
              criterion("Darunter steht ein Absatz in der Vorlage „Untertitel“", untertitel.length > 0),
            ],
            {
              success: "Dein Dokument hat jetzt einen richtigen Kopf.",
              failure: "Titel oder Untertitel fehlen noch.",
              hint: "Beide Vorlagen stehen im Katalog meist weiter hinten. Über den kleinen Pfeil rechts unten in der Gruppe „Formatvorlagen“ siehst du die vollständige Liste.",
            },
          );
        },
      },
    },
    {
      id: "zitate",
      title: "Zitate hervorheben",
      explanation: [
        "Für wörtlich übernommene Passagen gibt es „Zitat“ und „Intensives Zitat“. Beide " +
          "rücken den Text ein und setzen ihn kursiv, sodass er sich klar vom eigenen " +
          "Text abhebt.",
        "Auch hier gilt das Grundprinzip des Moduls: Du könntest den Absatz von Hand " +
          "einrücken und kursiv setzen – aber nur mit der Vorlage bleibt jedes Zitat im " +
          "Dokument identisch, und du kannst das Aussehen aller Zitate auf einen Schlag " +
          "ändern.",
      ],
      keyPoints: [
        "„Zitat“ für übernommene Passagen, „Intensives Zitat“ für herausgestellte Aussagen.",
        "Für Aufzählungen übernimmt Word automatisch die Vorlage „Listenabsatz“.",
      ],
      task: {
        instruction: "Füge deinem Dokument einen Absatz in der Vorlage „Zitat“ hinzu.",
        hint: "„Intensives Zitat“ wird ebenfalls anerkannt.",
        check: async () => {
          const snapshot = await readDocument();
          const zitate = [
            ...paragraphsWithStyle(snapshot, "Quote"),
            ...paragraphsWithStyle(snapshot, "IntenseQuote"),
          ];

          if (zitate.length === 0) {
            return fail(
              "Es gibt noch keinen Absatz in einer Zitatvorlage.",
              "Schreibe einen Satz, setze den Cursor hinein und wähle im Katalog „Zitat“.",
            );
          }
          return pass(`Zitat erkannt: „${zitate[0].text.slice(0, 60)}“.`);
        },
      },
    },
  ],
};
